import type { LoaderFunctionArgs } from '~/types/route-args';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
// eslint-disable-next-line folders/match-regex

import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getPreloadImageLink } from '~/modules/images';
import { createBlogSchema } from '~/modules/seo/schema/blog-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';

import FeaturedSection from '@modules/articles/component/featured-section/FeaturedSection';
import LatestArticles from '@modules/articles/component/latest-articles/LatestArticles';
import FilmsSection from '@modules/articles/component/films-section/FilmsSection';
import { useLoaderData } from 'react-router';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import dayjs from 'dayjs';
import { fillExtraInfoForArticleDirectory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import {
    FILMS_CATEGORY_LINK,
    LATEST_ARTICLES_PAGE_SIZE,
    LATEST_FILMS_PAGE_SIZE,
    ROOT_CATEGORY_LINK,
} from '@modules/articles/config/constants';
import type { FeaturedCategorySection } from '@modules/articles/type';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import PageSections from '@modules/articles/component/page-layout/PageSections';
import type { APHandle } from '@modules/root';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { getArticleDirectory } from '~/modules/articles/api/get-article-directory.server';
import { getDirectoryFeaturedArticles } from '~/modules/articles/api/get-directory-featured-articles.server';
import { getLatestArticles } from '~/modules/articles/api/get-latest-articles.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getDigitalPublicationOptions } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { placeholderDigitalPublications } from '@modules-rocco/subscription/config/placeholder-publications';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { ArticlesDirectoryRevamp } from '@modules/articles-revamp/component/ArticlesDirectoryRevamp';
import {
    EDITORIAL_GROUND,
    EDITORIAL_STATEMENT_FALLBACK,
} from '@modules/articles-revamp/config/editorial-ground';
import { isArticlesRevampEnabled } from '@modules/articles-revamp/util/is-articles-revamp-enabled.server';
import { buildArticlesFilterState } from '@modules/articles-revamp/api/build-articles-filter-state.server';
import { searchArticles } from '@modules/articles-revamp/api/search-articles.server';
import { ARTICLE_SEARCH_PARAM } from '@modules/articles-revamp/config/article-search';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        const title = createMetaTitle(props, [data?.localisation?.Title]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const blogSchema = createBlogSchema(
            props as unknown as Parameters<typeof createBlogSchema>[0],
        );
        const ldSchema = createLDSchema([blogSchema ?? []]);

        const { rootData } = getRouteData(matches);
        const isMobile = rootData?.device.type === 'mobile';
        const bannerImage =
            data?.articleDirectory?.featuredArticles[0]?.image.src;

        const preloadImages = isMobile
            ? getPreloadImageLink(undefined, bannerImage)
            : getPreloadImageLink(bannerImage);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...(preloadImages ?? []),
        ];
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    fadeTopNavAfterPosition: 200,
    // The revamp opens the way the category pages do — the solid header with
    // the logomark, the library's watermark reaching up beside it. Resolved
    // against loader data so the legacy directory keeps its plain header while
    // the flag is off.
    header: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? { showLogomark: true, ground: EDITORIAL_GROUND }
            : undefined,
    // The same paper on the shell's wrapper, server-rendered, so nothing
    // white shows between or behind the sections that carry it.
    contentClassName: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? EDITORIAL_GROUND
            : undefined,
};

const SHOW_FEATURE_CAROUSEL: string[] = [];

/** Slides in the directory's leading carousel. */
const FEATURED_CAROUSEL_SLIDES = 5;

/**
 * Slides the legacy featured strip is drawn for.
 *
 * Its own constant rather than TOP_ARTICLES_COUNT, which lives in a `.server`
 * module: Remix strips those from the client bundle, so naming one in the
 * component drops the whole route from that bundle and the page arrives
 * without any JavaScript at all.
 */
const LEGACY_FEATURED_SLIDES = 3;

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { user } = context;
    const requestUrl = request.url;

    // Only fetched behind the gate, so the legacy directory costs nothing extra.
    const showRevamp = isArticlesRevampEnabled(context, request);

    // The term committed in the rail's search field, if any. Its matches
    // take the place of the category sections; the rest of the page stands.
    const searchParams = new URL(request.url).searchParams;
    const searchTerm = showRevamp
        ? (searchParams.get(ARTICLE_SEARCH_PARAM)?.trim() ?? '')
        : '';
    const isSearching = searchTerm.length > 0;

    // A searched page shows its matches where the sections would be, so the
    // section content is not fetched for it. The phone masthead still leads a
    // searched page, so its sources — the directory's featured articles and
    // the banner placement — are kept on mobile; the desktop search view
    // renders neither hero nor sections and skips both.
    const needsSections = !isSearching;
    const needsHeroSources =
        !isSearching || context.appDisplayMode === 'mobile';

    // One round for everything the page needs: a search that waited for the
    // directory before it even started was most of the commit's cost.
    const [
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds,
        navItems,
        featuredArticlePicks,
        searchResults,
        catalogue,
    ] = await Promise.all([
        needsHeroSources ? getArticleDirectory(context) : null,
        needsSections
            ? getLatestArticles({
                  ...context,
                  categoryLink: ROOT_CATEGORY_LINK,
                  limit: LATEST_ARTICLES_PAGE_SIZE,
                  offset: 0,
              })
            : { articles: [], totalCount: 0 },
        needsSections
            ? getLatestArticles({
                  ...context,
                  categoryLink: FILMS_CATEGORY_LINK,
                  limit: LATEST_FILMS_PAGE_SIZE,
                  offset: 0,
              })
            : { articles: [], totalCount: 0 },
        getPageLocalisation(args),
        needsHeroSources
            ? adRequest({
                  type: 'ArchizeenBannerPlacement',
                  requestUrl,
                  ...args.context,
              })
            : null,
        getArticleNavigationItems(context),
        getDirectoryFeaturedArticles(context),
        isSearching
            ? searchArticles(context, {
                  term: searchTerm,
                  page: parseInt(searchParams.get('page') ?? '1', 10) || 1,
              })
            : null,
        showRevamp ? getDigitalPublicationOptions(context) : [],
    ]);

    // The carousel leads the page, so it keeps every slide the query returns:
    // there is no masthead above it holding the first one back.
    //
    // Its own count rather than TOP_ARTICLES_COUNT, which also splits the
    // legacy directory's featured sections — raising that to five would move
    // rows between sections on a page this change is not about.
    //
    // Three sources in order: a paid banner placement outranks everything, then
    // the articles staff have pinned to the hero, then the latest. The pinned
    // list is the editorial say in what leads the page; it is empty until
    // somebody uses it, so the page still works on an uncurated day.
    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const pinnedSlides = featuredArticlePicks.slides;
    const featuredSlides = (
        bannerAdsShuffled.length
            ? bannerAdsShuffled.map(mapAdDataToFeatureSlide)
            : pinnedSlides.length
              ? pinnedSlides
              : (articleDirectory?.featuredArticles ?? [])
    )
        .slice(0, FEATURED_CAROUSEL_SLIDES)
        /*
         * The masthead's date is formatted here rather than in the component.
         * `PublishDate` is ISO8601 with an offset, and `dayjs().format()`
         * renders in the runtime's own timezone — so formatting it in render
         * gives one day on the server (UTC) and another in the browser
         * (Pacific/Auckland) for anything published before noon NZ time. That
         * is a hydration mismatch, and this app answers one by discarding the
         * server HTML and re-rendering the document, which reads as the page
         * reloading itself. The loader only ever runs in one place, so the
         * string it sends is the string both sides use.
         */
        .map((slide) => ({
            ...slide,
            dateLabel: slide.date
                ? dayjs(slide.date).format('D MMMM YYYY')
                : undefined,
        }));

    // The publications row's catalogue arrives with the round above. Outside
    // production an empty catalogue falls back to placeholders: no
    // environment but production has publications configured, so the row would
    // never be reviewable otherwise.
    const publications =
        showRevamp && catalogue.length === 0
            ? placeholderDigitalPublications(context.env.stage)
            : catalogue;

    fillExtraInfoForArticleDirectory(
        user,
        articleDirectory,
        latestArticles.articles,
        latestFilms.articles,
    );

    // A different set on every load. Each section shows a dozen or so of its
    // category's articles, so dealt in publish order the same dozen led
    // every visit and the rest were never seen. Shuffled here, on the
    // server, so the page hydrates to what it was served — shuffled in the
    // browser it would repaint under the reader — and the directory reads
    // the way the other directories do, with as much of what it holds as a
    // visit can show.
    if (showRevamp && articleDirectory) {
        articleDirectory.featuredSections =
            articleDirectory.featuredSections.map((section) => ({
                ...section,
                articles: shuffleArray([...section.articles]),
            }));
    }

    return {
        deviceType: context.device.type,
        // The category rail's own state, so the directory can carry the same
        // bar the category pages do. Built from the URL and the nav alone —
        // no themes here, which are a category-page facet — so it costs no
        // extra round trip.
        filterStates: {
            ...buildArticlesFilterState({
                context,
                request,
                navItems: navItems ?? [],
                themes: [],
            }),
            totalCount: 0,
        },
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds: bannerAdsShuffled,
        navItems,
        featuredSlides,
        // The tile menu reads this to label itself Pin or Unpin. Sent whoever
        // is looking: it says which articles are pinned, not who may pin them,
        // and the menu that uses it only mounts for staff.
        heroPinnedArticleIDs: featuredArticlePicks.pinnedIDs,
        publications,
        showRevamp,
        searchResults,
        // The phone masthead: the featured carousel as a full-bleed hero
        // under a transparent header, the way the project detail page opens.
        // Decided here because the device is a server fact (mobile is UA, not
        // viewport) — the desktop composition keeps its solid paper header.
        // Gated on the slides too: with nothing featured the page opens on
        // the paper statement, and a transparent header over that paper
        // would draw light icons on a light ground.
        showMobileHero:
            showRevamp &&
            context.appDisplayMode === 'mobile' &&
            featuredSlides.length > 0,
        pageTheme:
            showRevamp &&
            context.appDisplayMode === 'mobile' &&
            featuredSlides.length > 0
                ? ('transparent' as const)
                : ('default' as const),
    };
};

const ArchizeenDirectoryPage = () => {
    const {
        articleDirectory,
        latestArticles,
        latestFilms,
        navItems,
        featuredSlides,
        publications,
        showRevamp,
        filterStates,
        heroPinnedArticleIDs,
        localisation,
        showMobileHero,
        searchResults,
    } = useLoaderData<typeof loader>();

    const { common } = useLoaderData<typeof archizeenRootLoader>();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);
    // The same for the shuffled sections: a revalidation deals them again,
    // and a grid that reshuffles under the reader is worse than one that
    // never changes.
    //
    // The search term is part of the memo key because the loader skips the
    // sections while a search is on. Without it, a page that mounts already
    // searched latches an empty list, and clearing the term back to the same
    // pathname keeps that empty list — the listing renders nothing.
    const sectionsMemoized = useLocationMemo(
        articleDirectory?.featuredSections ?? [],
        [ARTICLE_SEARCH_PARAM],
    );

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    // Gate sits below the hooks so both paths run the same hook order.
    if (showRevamp) {
        return (
            <>
                <ArticlesDirectoryRevamp
                    filterStates={filterStates}
                    navItems={navItems ?? []}
                    publications={publications}
                    heroPinnedArticleIDs={heroPinnedArticleIDs}
                    searchResults={searchResults}
                    // The featured carousel's slides, as the phone masthead.
                    // Empty unless the loader said this device gets one.
                    heroArticles={
                        showMobileHero
                            ? featuredSlidesMemoized.map((slide) => ({
                                  title: slide.title,
                                  link: slide.link,
                                  content: slide.content ?? undefined,
                                  categoryLabel:
                                      slide.categoryLabel ?? undefined,
                                  label: slide.label ?? undefined,
                                  date: slide.dateLabel,
                                  image: { src: slide.image?.src },
                              }))
                            : []
                    }
                    // The page's own description, which is what the meta
                    // description already says about it.
                    statement={
                        localisation?.Description ??
                        EDITORIAL_STATEMENT_FALLBACK
                    }
                    // The nav's categories and their articles in one list, so the
                    // tabs and the sections under them can never disagree.
                    categories={sectionsMemoized.map((section) => ({
                        title: section.title,
                        link: section.link,
                        articles: section.articles,
                    }))}
                />
            </>
        );
    }

    if (!articleDirectory) return null;

    const { featuredSections } = articleDirectory;
    const { articles: films, totalCount } = latestFilms;

    const loadMoreFilms =
        films.length < totalCount ? FILMS_CATEGORY_LINK : undefined;

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={common?.contentTop}
            />
            <PageSections>
                <FeaturedCarousel
                    featuredSlides={featuredSlidesMemoized.slice(
                        0,
                        LEGACY_FEATURED_SLIDES,
                    )}
                    imageLazyLoading={'eager'}
                />
                <LatestArticles
                    title={'The Latest'}
                    latestArticles={latestArticles.articles}
                />
                {featuredSections.map((section) => (
                    <FeaturedSection
                        key={section.link}
                        featuredSection={section as FeaturedCategorySection}
                        showFeaturedSlides={SHOW_FEATURE_CAROUSEL.includes(
                            section.title.toLowerCase(),
                        )}
                    />
                ))}
                {latestFilms.articles.length > 0 && (
                    <FilmsSection
                        latestArticles={latestFilms.articles}
                        loadMoreLink={loadMoreFilms}
                    />
                )}
            </PageSections>
        </>
    );
};

export const shouldRevalidate = shouldRevalidateLeaf;

export default ArchizeenDirectoryPage;
